import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2, Plus, ScanSearch, Undo2, WandSparkles, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { MIN_SCORE_MAX, MIN_SCORE_STEP } from './detected-holds';
import { WAND_TOLERANCE_MAX, WAND_TOLERANCE_MIN } from './colour-wand';

/**
 * The wall's confidence slider: holds the detector scored under `value` are
 * hidden. `onChange` is every step of a drag, `onCommit` the release.
 */
export interface ScanConfidence {
  /** The threshold in force, or null when nothing is hidden by it. */
  value: number | null;
  /** The lowest the slider goes: the lowest score on the wall. */
  floor: number;
  hidden: number;
  total: number;
  saving: boolean;
  onChange: (value: number | null) => void;
  onCommit: (value: number | null) => void;
}

/**
 * Looking again at an area of the wall for holds the scan missed. `drawing`
 * is the setter having armed it: the next drag on the photo draws the area.
 * While `running`, an area is being looked at and no other may be asked for.
 */
export interface ScanAreaControl {
  running: boolean;
  drawing: boolean;
  onDrawingChange: (drawing: boolean) => void;
}

/**
 * Taking holds off the wall by hand: the ones lit on the photo (`chosen`),
 * added to those already excluded (`excluded`).
 */
export interface ScanExclusion {
  chosen: number;
  excluded: number;
  saving: boolean;
  onExclude: () => void;
  onRestore: () => void;
  /**
   * Whether the photo shows the excluded holds instead of the wall's — the
   * count is the switch — so one can be pressed and put back.
   */
  showing?: boolean;
  onShowingChange?: (showing: boolean) => void;
  /** An excluded hold was pressed: asking whether to put it back. */
  restoring?: boolean;
  onAnswerRestore?: (restore: boolean) => void;
}

/**
 * Adding a hold by hand with the colour-wand: a point pressed on the photo,
 * grown into the patch of its colour within `tolerance`. `status` is where
 * that stands — the photo being read, waiting for a point, or the patch as it
 * came out.
 */
export interface ScanWand {
  active: boolean;
  onActiveChange: (active: boolean) => void;
  tolerance: number;
  onToleranceChange: (tolerance: number) => void;
  status: 'loading' | 'failed' | 'pick' | 'overflow' | 'tooSmall' | 'ready';
  adding: boolean;
  onAdd: () => void;
}

interface ScanWallToolsProps {
  /** Absent where nothing on the wall is scored. */
  confidence?: ScanConfidence;
  exclusion?: ScanExclusion;
  wand?: ScanWand;
  /** Absent where the detector cannot look at an area. */
  areaScan?: ScanAreaControl;
}

/**
 * **What is done to the wall itself**, as opposed to a climb on it: which of
 * the detected holds it offers, and asking for the ones it missed. In the
 * controls column above the scan's settings, and only while no climb is open —
 * the column says one thing at a time.
 */
export function ScanWallTools({ confidence, exclusion, wand, areaScan }: ScanWallToolsProps) {
  if (!confidence && !exclusion && !wand && !areaScan) return null;

  return (
    <section className="space-y-4 rounded-md border p-3" data-testid="scan-wall-tools">
      {confidence && <ConfidenceControl confidence={confidence} />}
      {exclusion && <ExclusionControl exclusion={exclusion} />}
      {wand && <WandControl wand={wand} />}
      {areaScan && <AreaScanControl areaScan={areaScan} />}
    </section>
  );
}

/**
 * **How sure the detector has to have been for a hold to be offered.**
 * Dragging hides the holds scored under it on the spot; letting go saves it on
 * the scan, for the next setter and the member app's editor. Hidden is not
 * deleted: moving it back brings them back, and a climb already made keeps the
 * holds it took.
 *
 * The holds the detector gets wrong — the ceiling, a skylight, an empty box
 * where it had no mask — score low; so do the smallest footholds, which is why
 * the line is a setter's to draw and not a constant (scan 18, #317).
 */
function ConfidenceControl({ confidence }: { confidence: ScanConfidence }) {
  const { t } = useTranslation();
  const { value, floor, hidden, total, saving, onChange, onCommit } = confidence;
  // At the floor nothing is hidden, and that is "no threshold" rather than one.
  const asThreshold = (raw: number) => (raw <= floor + MIN_SCORE_STEP / 2 ? null : raw);
  const percent = (score: number) => `${Math.round(score * 100)}%`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span id="scan-confidence-label" className="text-sm font-medium">
          {t('gymAdmin.scans.confidence.label')}
        </span>
        <span
          className="flex items-center gap-1.5 text-sm tabular-nums text-muted-foreground"
          data-testid="scan-confidence-value"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
          {value == null ? t('gymAdmin.scans.confidence.all') : `≥ ${percent(value)}`}
        </span>
      </div>
      <Slider
        aria-labelledby="scan-confidence-label"
        min={floor}
        max={Math.max(MIN_SCORE_MAX, floor)}
        step={MIN_SCORE_STEP}
        value={[value ?? floor]}
        onValueChange={([raw]) => onChange(asThreshold(raw))}
        onValueCommit={([raw]) => onCommit(asThreshold(raw))}
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm" data-testid="scan-confidence-hidden">
          {hidden > 0
            ? t('gymAdmin.scans.confidence.hidden', { count: hidden, total })
            : t('gymAdmin.scans.confidence.noneHidden')}
        </p>
        {value != null && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            disabled={saving}
            onClick={() => onCommit(null)}
          >
            {t('gymAdmin.scans.confidence.showAll')}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('gymAdmin.scans.confidence.explain')}</p>
    </div>
  );
}

/**
 * **What the slider cannot reach**: a wrong hold the detector was sure of, or
 * one real hold among the low-scored ones a setter wants kept. Lit on the
 * photo — the same press that gathers a climb's holds — then excluded here.
 * Hidden like the slider's, and put back all at once — or one at a time:
 * the count shows the excluded holds on the photo, and a press on one asks
 * whether to put it back.
 */
function ExclusionControl({ exclusion }: { exclusion: ScanExclusion }) {
  const { t } = useTranslation();
  const { chosen, excluded, saving, onExclude, onRestore, showing, onShowingChange, restoring, onAnswerRestore } =
    exclusion;

  return (
    <div className="space-y-2">
      {!showing && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2"
          disabled={chosen === 0 || saving}
          onClick={onExclude}
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <EyeOff className="size-4" aria-hidden />
          )}
          {chosen > 0
            ? t('gymAdmin.scans.exclude.chosen', { count: chosen })
            : t('gymAdmin.scans.exclude.button')}
        </Button>
      )}
      {chosen === 0 && (
        <p className="text-xs text-muted-foreground">
          {t(showing ? 'gymAdmin.scans.exclude.showingHint' : 'gymAdmin.scans.exclude.hint')}
        </p>
      )}
      {excluded > 0 && (
        <div className="flex items-center justify-between gap-2">
          {/* The count is the way to see them: pressed, the photo shows the
              excluded holds, and a press on one offers to put it back. */}
          {onShowingChange ? (
            <Button
              type="button"
              variant={showing ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 gap-1.5 px-2 text-sm font-normal"
              aria-pressed={!!showing}
              data-testid="scan-excluded-count"
              onClick={() => onShowingChange(!showing)}
            >
              <Eye className="size-3.5" aria-hidden />
              {t('gymAdmin.scans.exclude.count', { count: excluded })}
            </Button>
          ) : (
            <p className="text-sm" data-testid="scan-excluded-count">
              {t('gymAdmin.scans.exclude.count', { count: excluded })}
            </p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2"
            disabled={saving}
            onClick={onRestore}
          >
            <Undo2 className="size-3.5" aria-hidden />
            {t('gymAdmin.scans.exclude.restore')}
          </Button>
        </div>
      )}

      <AlertDialog open={!!restoring} onOpenChange={(open) => !open && onAnswerRestore?.(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gymAdmin.scans.exclude.restoreOne.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('gymAdmin.scans.exclude.restoreOne.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="scan-restore-one" onClick={() => onAnswerRestore?.(true)}>
              {t('gymAdmin.scans.exclude.restoreOne.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * **A hold the detector never saw, added by hand.** Armed here; then a press
 * on the photo picks a point, the patch of its colour is drawn over the wall,
 * the tolerance widens or narrows it on the spot, and "add" makes it a hold.
 * Stays armed after adding, for the next one.
 */
function WandControl({ wand }: { wand: ScanWand }) {
  const { t } = useTranslation();
  const { active, onActiveChange, tolerance, onToleranceChange, status, adding, onAdd } = wand;

  if (!active) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full gap-2"
        onClick={() => onActiveChange(true)}
      >
        <WandSparkles className="size-4" aria-hidden />
        {t('gymAdmin.scans.wand.start')}
      </Button>
    );
  }

  return (
    <div className="space-y-3" data-testid="scan-wand">
      <p
        role="status"
        className={`flex items-center gap-2 text-sm ${
          status === 'failed' || status === 'overflow' ? 'text-destructive' : 'text-muted-foreground'
        }`}
      >
        {status === 'loading' && <Loader2 className="size-4 animate-spin" aria-hidden />}
        {t(`gymAdmin.scans.wand.status.${status}`)}
      </p>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span id="scan-wand-tolerance-label" className="text-sm font-medium">
            {t('gymAdmin.scans.wand.tolerance')}
          </span>
          <span className="text-sm tabular-nums text-muted-foreground">{tolerance}</span>
        </div>
        <Slider
          aria-labelledby="scan-wand-tolerance-label"
          min={WAND_TOLERANCE_MIN}
          max={WAND_TOLERANCE_MAX}
          step={1}
          value={[tolerance]}
          onValueChange={([value]) => onToleranceChange(value)}
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          className="flex-1 gap-2"
          disabled={status !== 'ready' || adding}
          onClick={onAdd}
        >
          {adding ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Plus className="size-4" aria-hidden />}
          {t('gymAdmin.scans.wand.add')}
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => onActiveChange(false)}>
          {t('gymAdmin.scans.wand.done')}
        </Button>
      </div>
    </div>
  );
}

/**
 * The press that arms "draw round the missing holds". The drawing itself
 * happens on the photo, which says so while it lasts (ScanPlaceholderPicker);
 * this is where it is started, cancelled, and seen running — from seconds to
 * a minute or two, one area at a time per wall.
 */
function AreaScanControl({ areaScan }: { areaScan: ScanAreaControl }) {
  const { t } = useTranslation();
  const { running, drawing, onDrawingChange } = areaScan;

  if (running) {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {t('gymAdmin.scans.areaScan.running')}
      </p>
    );
  }

  return drawing ? (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t('gymAdmin.scans.areaScan.hint')}</p>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full gap-2"
        onClick={() => onDrawingChange(false)}
      >
        <X className="size-4" aria-hidden />
        {t('gymAdmin.scans.areaScan.cancel')}
      </Button>
    </div>
  ) : (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="w-full gap-2"
      onClick={() => onDrawingChange(true)}
    >
      <ScanSearch className="size-4" aria-hidden />
      {t('gymAdmin.scans.areaScan.start')}
    </Button>
  );
}
